/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import MDBox from "components/MDBox";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import { Tab, Tabs } from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";

import Loader from "utils/Loader";
import { getPointsTextColor } from "utils/HelperFunctions";
import { hexToRgba } from "utils/GenerateColorFromString/GenerateColorFromString";

import WeeklyTotals from "./WeeklyTotals";
import TableListingForManager from "./TableListingForManager";
import CircularProgressChartForCompany from "./CircularProgressChartForCompany";
import TableListingForManagerGridView from "./TableListingForManagerGridView";
import TableListingForManagerListView from "./TableListingForManagerListView";

function TeamView({
  selectedTab,
  selectedUser,
  selectedTeam,
  setSelectedTeam,
  handleUserClick,
  selectedTeams,
}) {
  // const user = useSelector((state) => state.loginUser.loginUserState);

  const {
    data: teamMemberPerformance,
    loading: teamMemberPerformanceLoading,
    streakData,
  } = useSelector((state) => state.manager.teamMemberPerformance); // api response data

  const [convertedTeamData, setConvertedTeamData] = useState([]); // it saved converted teamMemberPerformance data into component specific structure
  const [selectedTeamListView, setSelectedTeamListView] = useState(0); // use to identify current team view tab
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("percentage");

  const handleTabChange = (_, newValue) => {
    if (newValue > 1) return;
    setSelectedTeamListView(newValue);
  };

  /** on api response change convert that data into component specific structure and saved it in convertedTeamData */
  useEffect(() => {
    const newConvertedTeamData =
      teamMemberPerformance
        .map((teamData) => {
          let concatUsers = [];
          teamData.users.forEach((kpi) => {
            concatUsers = concatUsers.concat(kpi.totalPerformance);
          });

          const totalAchievedPoints = parseInt(
            concatUsers.reduce((acc, obj) => acc + (obj ? parseInt(obj.points, 10) : 0), 0),
            10
          );

          const totalKpiCompleted = parseInt(
            concatUsers.reduce(
              (acc, obj) => acc + (obj ? parseInt(obj.totalKpiCompleted, 10) : 0),
              0
            ),
            10
          );

          const totalCompletedKpis = parseInt(
            concatUsers.reduce(
              (acc, obj) =>
                acc + (obj && obj.inputKPIPermission ? parseInt(obj.kpiTargetPoints, 10) : 0),
              0
            ),
            10
          );

          const percentage = (totalAchievedPoints / totalCompletedKpis) * 100;

          const hexColor = getPointsTextColor(percentage);
          const barOptions = {
            maintainAspectRatio: false,
            responsive: true,
            indexAxis: "y",
            animation: false,
            scales: {
              x: {
                display: false,
                stacked: false,
                min: 0,
                max: Math.max(totalAchievedPoints, totalCompletedKpis, 1),
              },
              y: {
                display: false,
                stacked: true,
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              title: {
                display: false,
              },
              tooltip: {
                enabled: false,
              },
            },
          };

          const barData = {
            labels: [""],
            datasets: [
              {
                label: "count",
                backgroundColor: hexColor,
                hoverBackgroundColor: hexColor,
                data: [totalAchievedPoints],
                borderRadius: 100,
                borderSkipped: false,
              },
              {
                label: "target",
                backgroundColor: totalCompletedKpis ? hexToRgba(hexColor, 0.2) : "#fafafa",
                hoverBackgroundColor: totalCompletedKpis ? hexToRgba(hexColor, 0.2) : "#fafafa",
                data: [Math.max(totalCompletedKpis, 1)],
                borderRadius: 100,
                borderSkipped: false,
              },
            ],
          };

          return {
            ...teamData,
            percentage,
            totalAchievedPoints,
            totalCompletedKpis,
            totalKpiCompleted,
            hexColor,
            barOptions,
            barData,
          };
        })
        .sort((a, b) => b.totalAchievedPoints - a.totalAchievedPoints) || [];

    setConvertedTeamData(newConvertedTeamData);

    // Show team user view if only 1 team is filtered
    if (newConvertedTeamData.length === 1) {
      setSelectedTeam(newConvertedTeamData[0]);
    }
  }, [teamMemberPerformance]);

  return (
    <MDBox mb={2} sx={{ position: "relative" }}>
      <Loader loading={teamMemberPerformanceLoading} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {teamMemberPerformance && (
            <CircularProgressChartForCompany
              teamWiseTotalPerformance={teamMemberPerformance}
              selectedTeam={selectedTeam}
              selectedUser={selectedUser}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <WeeklyTotals
            selectedUser={selectedUser}
            selectedTab={selectedTab}
            view="team"
            selectedTeam={selectedTeam}
            selectedTeams={selectedTeams}
          />
        </Grid>
      </Grid>

      {!selectedTeam ? (
        <Grid container my={2} alignItems="center">
          <Grid item xs={6} sm={4} md={2}>
            <MDBox sx={{ alignSelf: "center", justifySelf: "center" }}>
              <Tabs
                value={selectedTeamListView}
                onChange={handleTabChange}
                sx={{
                  borderRadius: "none",
                }}
              >
                <Tab
                  icon={
                    <GridViewIcon
                      sx={{
                        margin: "0 .6rem",
                      }}
                      fontSize="medium"
                    />
                  }
                />
                <Tab
                  icon={<FormatListBulletedIcon sx={{ margin: "0 .6rem" }} fontSize="medium" />}
                />
              </Tabs>
            </MDBox>
          </Grid>
        </Grid>
      ) : null}

      {!selectedTeam && selectedTeamListView === 0 ? (
        <TableListingForManagerGridView
          data={convertedTeamData}
          setSelectedTeam={setSelectedTeam}
        />
      ) : null}

      {!selectedTeam && selectedTeamListView === 1 ? (
        <TableListingForManagerListView
          data={convertedTeamData}
          setSelectedTeam={setSelectedTeam}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
        />
      ) : null}

      {selectedTeam ? (
        <TableListingForManager
          data={selectedTeam}
          streakData={streakData}
          onUserClick={handleUserClick}
          selectedUserId={selectedUser}
          setSelectedTeam={setSelectedTeam}
        />
      ) : null}
    </MDBox>
  );
}

export default TeamView;

TeamView.propTypes = {
  selectedTab: PropTypes.number.isRequired,
  selectedUser: PropTypes.number.isRequired,
  handleUserClick: PropTypes.func.isRequired,
};
